import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { ImageItem } from '@backbase/foundation-ang/web-sdk';
import { ContentService } from '@backbase/universal-ang/content';
import { first, map, takeUntil } from 'rxjs/operators';
import { CategoryImage, Transaction } from './interfaces';
import { Category } from './enums';
import { TransactionsExportTypeElement } from '@backbase/transactions-common-ang/src/model/transactions-export-options.model';
import { TransactionItem } from '@backbase/transactions-http-ang';
import { ArrangementsHttpService } from '@backbase/data-ang/arrangements';
import { AccountSelectorWidgetExtendedService } from 'account-selector-widget-extended';

@Component({
  selector: 'bn-transactions-list-widget-extended',
  templateUrl: 'transactions-list-widget-extended.component.html',
  providers: [ContentService],
})
export class TransactionsListWidgetExtendedComponent implements OnInit, OnDestroy {
  @Input() hostRef: any;
  private destroy$ = new Subject<boolean>();
  readonly lastTransactionRecordIndicator = 'FIN REGISTRO';
  readonly categoryTypes = Object.keys(Category);
  readonly exportConfig: TransactionsExportTypeElement[] = [
    {
      name: 'PDF',
      isAvailable: true,
    },
    {
      name: 'CSV',
      isAvailable: true,
    },
  ];
  public iconListObject: CategoryImage | undefined;
  public isModalOpen = false;
  public currentTransaction: TransactionItem | undefined;
  public showBalanceData = true;
  public accountType: string | undefined = '';

  constructor(
    private readonly contentService: ContentService,
    private accountSelectorService: AccountSelectorWidgetExtendedService,
    private arrangementService: ArrangementsHttpService,
  ) {}

  ngOnInit(): void {
    this.loadCategoryItemIcons();
    this.accountSelectorService.selectedAccount
      .pipe(takeUntil(this.destroy$))
      .subscribe((account) => this.getAccountDetails(account));
  }

  getAccountDetails(arrangementId: string) {
    this.arrangementService.getArrangementById({ arrangementId }).subscribe((arrangement) => {
      this.accountType = arrangement?.product?.productKind?.externalKindId;
    });
  }

  /**
   * Load category item icons
   */
  loadCategoryItemIcons(): void {
    const iconCategoryList = this.categoryTypes.map((cat) => this.contentService.getContent<ImageItem>(cat));
    const defaultUrlValue = { url: '' };
    combineLatest(iconCategoryList)
      .pipe(
        first(),
        map(
          (iconListObject) =>
            this.categoryTypes.reduce(
              (accumulator, currentValue, index) => ({
                ...accumulator,
                [currentValue]: iconListObject[index] || defaultUrlValue,
              }),
              {},
            ) as CategoryImage,
        ),
      )
      .subscribe((iconListObject) => (this.iconListObject = iconListObject));
  }

  /**
   * get content icon
   * @param typeGroup
   */
  getUrlIcon(typeGroup: Category): string {
    switch (typeGroup) {
      case Category.servicios:
        return 'transaction-list-icon-transaction-list-icon-servicios';
      case Category.entretenimiento:
        return 'transaction-list-icon-entertainment';
      case Category.restaurantes:
        return 'transaction-list-icon-food_and_dinning';
      case Category.viajes:
        return 'transaction-list-icon-travel';
      case Category.otros:
        return 'transaction-list-icon-uncategorized';
      case Category.educacion:
        return 'transaction-list-icon-education';
      case Category.cuidadoPersonal:
        return 'transaction-list-icon-personal_care';
      case Category.tiendas:
        return 'transaction-list-icon-shopping';
      case Category.impuestos:
        return 'transaction-list-icon-taxes';
      case Category.transferencia:
        return 'transaction-list-icon-transferencias';
      case Category.salud:
        return 'transaction-list-icon-salud';
      case Category.combustible:
        return 'transaction-list-icon-combustible';
      default:
        return 'transaction-list-icon-uncategorized';
    }
  }

  /**
   * Open modal details for selected transaction
   * @param transaction
   */
  openModalDetails(transaction: TransactionItem): void {
    this.currentTransaction = transaction;
    if (this.currentTransaction?.additions) {
      this.currentTransaction.additions.accountType = this.accountType || '';
    }
    this.isModalOpen = true;
  }

  /**
   * Set variable showBalanceDAta
   * @param showBalanceData
   */
  setShowBalanceData(showBalanceData: boolean): void {
    this.showBalanceData = showBalanceData;
  }

  /**
   * on close modal function
   */
  closeModalDetails(): void {
    this.isModalOpen = false;
  }

  /**
   * Validates if the las transaction is the end of list
   * @param transactions
   * @param totalCount
   */
  hasLoadedAll(transactions: Transaction[], totalCount: number): boolean {
    return transactions.length >= totalCount;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }
}
